import React, {useEffect} from 'react';
import {
  LeftColumnContent,
  RightColumnContent,
  TwoColumnGrid,
} from './components';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  Text,
  Textarea,
} from '@chakra-ui/react';
import {ScrollableFlex, WrapperFlex} from '../../utils/scrolling';
import {AgentBuilderPageIndicator} from './AgentBuilderPageIndicator';
import {useAgentNavigation} from './AgentBuilderNav.state';
import {
  LegacyAiResearchFieldMode,
  LegacyAiResearchFieldModeLabels,
  LegacyAiResearchFieldModeSchema,
  isLegacyAiResearchFieldDefinition,
} from '../../../shared/enrichment';
import {useCustomer} from '../../../hooks/api/metadata';
import {useFieldDefinitionEditorStore} from './AiFieldDefinitionEditor.state';
import {
  useAgentBuilderStore,
  useAgentBuilderServerState,
} from './AgentBuilder.state';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';
import {BuilderFooter} from './BuilderFooter';
import {ConfigFormLabel} from './ConfigFormLabel';
import {BuilderPreviewCardList} from './BuilderPreviewCardList';
import {AgentBuilderPreviewButton} from './AgentBuilderPreviewButton';
import {useIsEnrichingAgent} from '../../../hooks/api/enrichment';
import {StepContainer} from './StepContainer';
import {getAiFieldModel} from '../../../shared/agent';

export const AgentBuilderEditLegacyResearchFieldStep = () => {
  const {
    id: agentId,
    type: agentType,
    status: agentStatus,
  } = useAgentBuilderServerState();
  const {previewAccountIds} = useAgentBuilderStore();
  const {isSaveable, updateServerState, publishChanges} =
    useFieldDefinitionEditorStore();

  const fieldDefinitions = useCustomer().fieldDefinitions ?? [];
  const agentResearchField = fieldDefinitions
    .filter(isLegacyAiResearchFieldDefinition)
    .find((fieldDefinition) => fieldDefinition.agentId?.equals(agentId));

  useEffect(() => {
    if (agentResearchField) {
      updateServerState({
        serverState: agentResearchField,
        updateMode: 'ifNoChanges',
      });
    }
  }, [agentResearchField, updateServerState]);

  const onNavigate = useAgentNavigation({
    mutation: publishChanges,
    saveOnBack: agentStatus === 'draft' && isSaveable(),
  });

  return (
    <StepContainer isSaveable={isSaveable()} onSaveMutation={publishChanges}>
      <TwoColumnGrid>
        <Flex
          borderRight="1px solid"
          borderColor="kgray.200"
          direction="column"
        >
          <Box position="relative" h="100%">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <LeftColumnContent>
                  <AgentBuilderPageIndicator />
                  <LegacyResearchFieldEditor />
                </LeftColumnContent>
              </ScrollableFlex>
            </WrapperFlex>
          </Box>

          <Divider />

          <BuilderFooter justifyContent="space-between">
            <Button
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('prev')}
              variant="ghost"
            >
              Back
            </Button>

            <Button
              colorScheme="kbuttonblue"
              isDisabled={!isSaveable()}
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('next')}
            >
              {agentType === 'legacyResearchAndAnalyst' ||
              agentType === 'researchAndAnalyst'
                ? 'Continue to Analyst'
                : 'Continue to Review'}
            </Button>
          </BuilderFooter>
        </Flex>

        <RightColumnContent>
          <Flex alignItems="baseline" justifyContent="space-between" mt="-6px">
            <Text fontSize="xl">Preview</Text>
            <AgentBuilderPreviewButton accountIds={previewAccountIds} />
          </Flex>

          <Box flex="1" position="relative">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <BuilderPreviewCardList
                  accountIds={previewAccountIds}
                  fieldDefinition={agentResearchField}
                  isPreviewDisabled={!isSaveable()}
                />
              </ScrollableFlex>
            </WrapperFlex>
          </Box>
        </RightColumnContent>
      </TwoColumnGrid>
    </StepContainer>
  );
};

const LegacyResearchFieldEditor = () => {
  const {id: agentId, status} = useAgentBuilderServerState();
  const {data: isEnrichingAgent} = useIsEnrichingAgent({agentId});
  const isDisabled = status === 'published' || isEnrichingAgent;
  const {config, setConfig} = useFieldDefinitionEditorStore();
  const isAdmin = useIsKeyplayAdmin();

  if (config && config.fieldType !== 'legacyResearch') {
    return <>Field type is not research</>;
  }

  // only admins can select beast mode
  const isModeLocked = !isAdmin && config?.mode === 'beast';
  // if not an admin, only show beast mode if it's the initial mode (non-admins can't change it)
  const showBeastMode = isAdmin || config?.mode === 'beast';

  return (
    <Flex direction="column" gap="6">
      <Text fontSize="xl">Prompt your Researcher</Text>

      <FormControl>
        <ConfigFormLabel>Research Mode</ConfigFormLabel>
        <Select
          value={config?.mode ?? ('structured' as const)}
          isDisabled={isDisabled || isModeLocked}
          onChange={(e) => {
            setConfig({
              ...(config
                ? config
                : {
                    analysisFields: [],
                    model: getAiFieldModel(),
                    fieldType: 'legacyResearch',
                    prompt: {input: ''},
                    tools: ['perplexity'],
                  }),
              mode: e.target.value as LegacyAiResearchFieldMode,
            });
          }}
        >
          {LegacyAiResearchFieldModeSchema.options
            .filter((mode) => mode !== 'beast' || showBeastMode)
            .map((mode) => (
              <option key={mode} value={mode}>
                {LegacyAiResearchFieldModeLabels[mode]}
              </option>
            ))}
        </Select>
        {isModeLocked && (
          <FormHelperText textColor="kgray.300">
            Beast Mode is currently in beta. Please contact Keyplay support for
            assistance.
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm" textTransform="uppercase">
          Research Question
        </FormLabel>
        <Textarea
          isDisabled={isDisabled}
          onChange={(e) => {
            setConfig({
              ...(config
                ? config
                : {
                    analysisFields: [],
                    model: getAiFieldModel(),
                    fieldType: 'legacyResearch',
                    mode: 'structured',
                    tools: ['perplexity'],
                  }),
              prompt: {
                input: e.target.value,
              },
            });
          }}
          rows={5}
          placeholder="Ask your AI analyst anything..."
          value={config?.prompt.input ?? ''}
        />
        <FormHelperText textColor="kgray.300">
          Prompting is most successful when you phrase your prompt as a question
          about a single account, like &quot;Does this company have brick and
          mortar locations?&quot;
        </FormHelperText>
      </FormControl>
    </Flex>
  );
};
